/**
 * React Hook for UI Event Logging
 * Provides easy integration with React components
 */

import { useEffect, useCallback, useRef } from 'react';
import uiEventLogger, { UIEventType, UIEventCategory } from '../services/uiEventLogger';

/**
 * Hook for logging page views automatically
 */
export function usePageViewLogger(pageName, metadata = {}) {
    useEffect(() => {
        uiEventLogger.logPageView(pageName, metadata);
    }, [pageName, metadata]);
}

/**
 * Hook for logging form interactions
 */
export function useFormLogger(formName) {
    const formStartTime = useRef(null);

    const logFormStart = useCallback(() => {
        formStartTime.current = Date.now();
        uiEventLogger.logFormStart(formName);
    }, [formName]);

    const logFormSubmit = useCallback((metadata = {}) => {
        const duration = formStartTime.current 
            ? Date.now() - formStartTime.current 
            : null;

        uiEventLogger.logFormSubmit(formName, {
            ...metadata,
            formDurationMs: duration,
        });
    }, [formName]);

    const logFormError = useCallback((errorMessage, metadata = {}) => {
        uiEventLogger.logFormError(formName, errorMessage, metadata);
    }, [formName]);

    const logValidationError = useCallback((fieldName, errorMessage) => {
        uiEventLogger.logValidationError(fieldName, errorMessage, {
            formName,
        });
    }, [formName]);

    return {
        logFormStart,
        logFormSubmit,
        logFormError,
        logValidationError,
    };
}

/**
 * Hook for logging API calls
 */
export function useApiLogger() {
    const apiCallTimes = useRef({});

    const logApiCall = useCallback((endpoint, method = 'POST', metadata = {}) => {
        const callId = `${Date.now()}`;
        apiCallTimes.current[callId] = Date.now();
        
        uiEventLogger.logApiCall(endpoint, method, {
            ...metadata,
            callId,
        });

        return callId;
    }, []);

    const logApiSuccess = useCallback((callId, endpoint, metadata = {}) => {
        const startTime = apiCallTimes.current[callId];
        const duration = startTime ? Date.now() - startTime : null;

        uiEventLogger.logApiSuccess(endpoint, duration, metadata);

        delete apiCallTimes.current[callId];
    }, []);

    const logApiError = useCallback((callId, endpoint, error, statusCode = null, metadata = {}) => {
        const startTime = apiCallTimes.current[callId];
        const duration = startTime ? Date.now() - startTime : null;

        uiEventLogger.logApiError(endpoint, error, statusCode, {
            ...metadata,
            durationMs: duration,
        });

        delete apiCallTimes.current[callId];
    }, []);

    return {
        logApiCall,
        logApiSuccess,
        logApiError,
    };
}

/**
 * Hook for logging button clicks
 */
export function useButtonLogger() {
    const logButtonClick = useCallback((buttonName, metadata = {}) => {
        uiEventLogger.logButtonClick(buttonName, metadata);
    }, []);

    return { logButtonClick };
}

/**
 * Global error boundary hook
 */
export function useErrorLogger() {
    useEffect(() => {
        const handleError = (event) => {
            uiEventLogger.logUnhandledError(event.error || new Error(event.message), {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        };

        const handleRejection = (event) => {
            uiEventLogger.logUnhandledError(new Error(event.reason), {
                type: 'unhandledRejection',
            });
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);
}

export default uiEventLogger;
